import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/modules/common/api/index.ts';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { CURRENT_ADMIN_ISSUERS_QUERY_KEY } from '@/modules/admin-dashboard/queries/issuers.tsx';
import { ISSUER_REGISTER_ISSUERS_LIST_QUERY_KEY } from '@/modules/issuer-dashboard/queries/registers.tsx';
import { UpdateUserPermissionRequest, UserPermission } from 'api';

export const USER_REGISTER_PERMISSIONS_QUERY_KEY = 'vestra/user-register-permissions';

export const useRegisterPermissionsQuery = (registerId?: number, disabled = false) => {
  const [params] = useSearchParams();
  const register = params.get(REGISTER_ID_SEARCH_PARAM_KEY) || registerId || '';
  return useQuery<UserPermission | null>({
    queryKey: [USER_REGISTER_PERMISSIONS_QUERY_KEY, register],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getUserPermissionV1SrUserPermissionGet({
        headers: register ? { 'xRegister-ID': register } : undefined,
      });
      return response.data?.data?.find(item => item.register_id === +register) || null;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useUpdateUserPermissionsMutation = () => {
  return useMutation({
    mutationFn: async (payload: UpdateUserPermissionRequest) => {
      const response = await withErrorHandling(
        api.v1.updateUserPermissionV1SrUserPermissionPatch(payload),
        {
          loading: 'Updating permissions...',
          success: 'Permissions updated!',
        },
      );

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_ADMIN_ISSUERS_QUERY_KEY]);
      queryClient.invalidateQueries([ISSUER_REGISTER_ISSUERS_LIST_QUERY_KEY]);
    },
  });
};
